import type { ReactNode } from "react";
import { filterClass } from "../../../utils/class-utils";
import { getTextAlignClass, resolveHorizontalContentAlign } from "../../utils/content-alignment";

type LatestItemsSharedClassOptions = {
  sectionClassName: string;
  bgClass?: string;
  paddingYClass?: string;
  cardBgClass?: string;
  cardBorderClass?: string;
  cardBorderColorClass?: string;
  contentAlignClass?: string;
  buttonColorClass?: string;
};

export function buildLatestItemsSharedClasses(options: LatestItemsSharedClassOptions) {
  const {
    sectionClassName,
    bgClass = "no-class",
    paddingYClass = "py-section-md",
    cardBgClass = "no-class",
    cardBorderClass = "no-class",
    cardBorderColorClass = "no-class",
    contentAlignClass = "no-class",
    buttonColorClass = "primary",
  } = options;

  const sectionClasses = [bgClass, filterClass(paddingYClass)]
    .map(filterClass)
    .filter(Boolean)
    .join(" ");

  const hasCardBorder = filterClass(cardBorderClass) || filterClass(cardBorderColorClass);
  const cardClasses = [
    "card",
    "rounded-4",
    "h-100",
    filterClass(cardBgClass),
    hasCardBorder ? "border" : "",
    filterClass(cardBorderClass),
    filterClass(cardBorderColorClass),
  ]
    .filter(Boolean)
    .join(" ");

  const buttonClasses = ["btn", "btn-sm", `btn-${buttonColorClass || "primary"}`].filter(Boolean).join(" ");
  const resolvedCardContentAlignClass = getTextAlignClass(
    resolveHorizontalContentAlign(contentAlignClass, undefined, "start")
  );

  return {
    section: ["section", sectionClassName, sectionClasses].filter(Boolean).join(" ").trim(),
    container: "container-xl",
    loading: "card shadow-sm",
    loadingBody: "card-body d-flex align-items-center gap-2",
    error: "alert alert-warning mb-0",
    editorNote: "alert alert-info mb-0",
    row: "row row-cols-1 row-cols-md-3 row-gap-5",
    col: "col",
    card: cardClasses,
    cardBody: ["card-body", "d-flex", "flex-column", resolvedCardContentAlignClass].filter(Boolean).join(" "),
    header: "d-flex flex-column gap-2 mb-4",
    title: "fs-24 mb-1",
    teaser: "card-text mb-0",
    cta: "mt-auto",
    ctaLink: buttonClasses,
    ctaDisabled: "btn btn-sm btn-outline-secondary",
    tableWrap: "table-responsive",
    table: "table table-striped table-hover align-middle mb-0",
    tableTitle: "fw-semibold",
    tableCta: buttonClasses,
    tableCtaDisabled: "btn btn-sm btn-outline-secondary",
  };
}

export type LatestItemsSharedClasses = ReturnType<typeof buildLatestItemsSharedClasses>;
type LatestItemsSurfaceClasses = Pick<LatestItemsSharedClasses, "section" | "container" | "loading" | "loadingBody" | "error" | "editorNote">;

export function LatestItemsLoadingState({ classes, message }: { classes: LatestItemsSurfaceClasses; message: string }) {
  return (
    <section className={classes.section}>
      <div className={classes.container}>
        <div className={classes.loading}>
          <div className={classes.loadingBody}>
            <span className="spinner-border spinner-border-sm" aria-hidden="true" />
            {message}
          </div>
        </div>
      </div>
    </section>
  );
}

export function LatestItemsErrorState({ classes, errorText }: { classes: LatestItemsSurfaceClasses; errorText: string }) {
  return (
    <section className={classes.section}>
      <div className={classes.container}>
        <div className={classes.error} role="alert">
          {errorText}
        </div>
      </div>
    </section>
  );
}

export function LatestItemsEditorNote({ classes, message }: { classes: LatestItemsSurfaceClasses; message: string }) {
  return (
    <div className={classes.editorNote} role="status">
      {message}
    </div>
  );
}

export function LatestItemsSectionFrame({
  classes,
  title,
  children,
}: {
  classes: Pick<LatestItemsSharedClasses, "section" | "container">;
  title: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className={classes.section}>
      <div className={classes.container}>
        <h2>{title}</h2>
        {children}
      </div>
    </section>
  );
}
